import React, { useState } from "react";
import {
  ManagerForm,
  ManagerFormTextField,
  ManagerFormNumberField,
  ManagerFormSelectField,
} from "../form/ManagerForm";
import Button from "../../../student/components/generic/button";
import { BrevoEventBody } from "./types";
import axios from "axios";
import { useMutation } from "@tanstack/react-query";
import { deltamathAPI } from "../../utils";
import { useDeltaToastContext } from "../../../shared/contexts/ToasterContext";

type EventType = BrevoEventBody["event"];

const BrevoEventForm: React.FC = () => {
  const toast = useDeltaToastContext();
  const [selectedEvent, setSelectedEvent] =
    useState<EventType>("accountCreated");

  const mutation = useMutation({
    mutationFn: (data: BrevoEventBody) => {
      return axios.post(
        `${deltamathAPI()}/manager_new/brevo/trigger-event`,
        data
      );
    },
    onSuccess: () => {
      toast.addToast({
        message: "Event triggered successfully",
        status: "Success",
      });
    },
    onError: () => {
      toast.addToast({
        message: "Error triggering event",
        status: "Error",
      });
    },
  });

  const handleFormSubmit = (data: any) => {
    const eventData: BrevoEventBody = {
      event: selectedEvent,
      payload: selectedEvent === "accountCreated" ? {} : data,
    } as BrevoEventBody;

    mutation.mutate(eventData);
  };

  const getDefaultValues = () => {
    switch (selectedEvent) {
      case "siteLicenseAdmin":
        return {
          renewal: "false",
          tier: "plus",
          adminHasExistingAccount: "false",
          integration: "",
          numOfIntegration: 0,
          siteName: "",
          adminTypePrimary: "true",
          expiration: "",
        };
      case "quoteActivated":
        return {
          quoteNum: "",
          siteName: "",
          quoteAdmin: "",
        };
      case "sendIndividualCode":
        return {
          tier: "plus",
          numLicenses: 1,
          siteName: "",
          accessCode: "",
          expiration: "",
        };
      case "inviteSiteTeacher":
        return {
          tier: "plus",
          hasExistingAccount: "false",
          integration: "",
          numOfIntegration: 0,
          siteName: "",
        };
      default:
        return {};
    }
  };

  const eventChoices: { value: EventType; label: string }[] = [
    { value: "accountCreated", label: "Account Created" },
    { value: "siteLicenseAdmin", label: "Site License Admin" },
    { value: "quoteActivated", label: "Quote Activated" },
    { value: "sendIndividualCode", label: "Send Individual Code" },
    { value: "inviteSiteTeacher", label: "Invite Site Teacher" },
  ];

  const tierChoices = [
    { value: "plus", label: "Plus" },
    { value: "integral", label: "Integral" },
  ];

  const booleanChoices = [
    { value: "true", label: "Yes" },
    { value: "false", label: "No" },
  ];

  const renderAccountCreatedForm = () => (
    <ManagerForm
      onSubmit={handleFormSubmit}
      defaultValues={getDefaultValues()}
      className="flex flex-col gap-6"
    >
      <div className="py-8 text-center">
        <p className="text-dm-charcoal-600">
          Account Created events have no additional payload data.
        </p>
      </div>
      <Button type="primary" submit>
        Submit Event
      </Button>
    </ManagerForm>
  );

  const renderSiteLicenseAdminForm = () => (
    <ManagerForm
      onSubmit={handleFormSubmit}
      defaultValues={getDefaultValues()}
      className="flex flex-col gap-6"
    >
      <div className="flex flex-col gap-6">
        <ManagerFormSelectField
          fieldName="renewal"
          label="Renewal"
          choices={booleanChoices}
          required
          helpText="Whether this is a renewal or not"
        />

        <ManagerFormSelectField
          fieldName="tier"
          label="Tier"
          choices={tierChoices}
          required
          helpText="License tier (plus or integral)"
        />

        <ManagerFormSelectField
          fieldName="adminHasExistingAccount"
          label="Admin Has Existing Account"
          choices={booleanChoices}
          required
          helpText="Whether the admin already has an account"
        />

        <ManagerFormTextField
          fieldName="password"
          label="Password"
          helpText="Password for new account"
        />

        <ManagerFormTextField
          fieldName="integration"
          label="Integration"
          helpText="Primary integration platform (schoology, canvas, classlink, clever, infinite campus, or blank)"
        />

        <ManagerFormNumberField
          fieldName="numOfIntegration"
          label="Number of Integrations"
          min={0}
          max={10}
          required
          helpText="Number of integration platforms (0, 1, 2, etc.)"
        />

        <ManagerFormTextField
          fieldName="siteName"
          label="Site Name"
          required
          helpText="Name of the school or district"
        />

        <ManagerFormSelectField
          fieldName="adminTypePrimary"
          label="Primary Admin"
          choices={booleanChoices}
          required
          helpText="Whether this is a primary admin (true) or non-primary admin (false)"
        />

        <ManagerFormTextField
          fieldName="expiration"
          label="Expiration Date"
          helpText="Expiration date in MM/DD/YYYY format)"
        />

        <Button type="primary" submit>
          Submit Event
        </Button>
      </div>
    </ManagerForm>
  );

  const renderQuoteActivatedForm = () => (
    <ManagerForm
      onSubmit={handleFormSubmit}
      defaultValues={getDefaultValues()}
      className="flex flex-col gap-6"
    >
      <div className="flex flex-col gap-6">
        <ManagerFormTextField
          fieldName="quoteNum"
          label="Quote Number"
          required
          helpText="Quote reference number"
        />

        <ManagerFormTextField
          fieldName="siteName"
          label="Site Name"
          required
          helpText="Name of the school or district"
        />

        <ManagerFormTextField
          fieldName="quoteAdmin"
          label="Quote Admin"
          required
          helpText="Name of the admin"
        />
      </div>
      <Button type="primary" submit>
        Submit Event
      </Button>
    </ManagerForm>
  );

  const renderSendIndividualCodeForm = () => (
    <ManagerForm
      onSubmit={handleFormSubmit}
      defaultValues={getDefaultValues()}
      className="flex flex-col gap-6"
    >
      <ManagerFormSelectField
        fieldName="tier"
        label="Tier"
        choices={tierChoices}
        required
        helpText="License tier (plus or integral)"
      />

      <ManagerFormNumberField
        fieldName="numLicenses"
        label="Number of Licenses"
        min={1}
        max={10000}
        required
        helpText="Number of licenses to be activated"
      />

      <ManagerFormTextField
        fieldName="siteName"
        label="Site Name"
        required
        helpText="Name of the school or district"
      />

      <ManagerFormTextField
        fieldName="accessCode"
        label="Access Code"
        required
        helpText="Access code for the licenses"
      />

      <ManagerFormTextField
        fieldName="expiration"
        label="Expiration Date"
        required
        helpText="Expiration date in MM/DD/YYYY format"
      />
      <Button type="primary" submit>
        Submit Event
      </Button>
    </ManagerForm>
  );

  const renderInviteSiteTeacherForm = () => (
    <ManagerForm
      onSubmit={handleFormSubmit}
      defaultValues={getDefaultValues()}
      className="flex flex-col gap-6"
    >
      <ManagerFormSelectField
        fieldName="tier"
        label="Tier"
        choices={tierChoices}
        required
        helpText="License tier (plus or integral)"
      />

      <ManagerFormSelectField
        fieldName="hasExistingAccount"
        label="Has Existing Account"
        choices={booleanChoices}
        required
        helpText="Whether the teacher already has an account"
      />

      <ManagerFormTextField
        fieldName="integration"
        label="Integration"
        helpText="Integration platform (schoology, canvas, classlink, clever, infinite campus, or blank)"
      />

      <ManagerFormNumberField
        fieldName="numOfIntegration"
        label="Number of Integrations"
        min={0}
        max={10}
        required
        helpText="Number of integration platforms (0, 1, 2, etc.)"
      />

      <ManagerFormTextField
        fieldName="siteName"
        label="Site Name"
        required
        helpText="Name of the school or district"
      />
      <Button type="primary" submit>
        Submit Event
      </Button>
    </ManagerForm>
  );

  return (
    <div className="max-w-2xl p-6">
      <h2 className="mb-6 text-2xl font-bold">Brevo Event Form</h2>

      <div className="mb-6">
        <label className="mb-2 block text-sm font-bold">
          Select Event Type
        </label>
        <select
          value={selectedEvent}
          onChange={(e) => setSelectedEvent(e.target.value as EventType)}
          className="h-9 w-full rounded border-dm-charcoal-200 px-2 py-0 text-sm"
        >
          {eventChoices.map((choice) => (
            <option key={choice.value} value={choice.value}>
              {choice.label}
            </option>
          ))}
        </select>
      </div>

      {selectedEvent === "accountCreated" && renderAccountCreatedForm()}
      {selectedEvent === "siteLicenseAdmin" && renderSiteLicenseAdminForm()}
      {selectedEvent === "quoteActivated" && renderQuoteActivatedForm()}
      {selectedEvent === "sendIndividualCode" && renderSendIndividualCodeForm()}
      {selectedEvent === "inviteSiteTeacher" && renderInviteSiteTeacherForm()}
    </div>
  );
};

export default BrevoEventForm;
